import React, { useMemo, useState } from 'react';

import {
  Grid,
  Typography,
  TimeLine,
  NoResults,
  TimeLineItem,
  TimeLineCollapse,
  LogText,
  IconButton,
  ImportExportIcon,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { dateFNS } from '@gogame/intl-ts';

import TimeLineContribution from './components/TimeLineContribution';
import TimeLineConversion from './components/TimeLineConversion';
import TimeLineEvent from './components/TimeLineEvent';
import TimeLineFraud from './components/TimeLineFraud';
import TimeLinePostBack from './components/TimeLinePostBack';
import TimeLineReengagement from './components/TimeLineReengagement';
import TimeLineRecurrentEvent from './components/TimeLineRecurrentEvent';
import TimeLineReferral from './components/TimeLineReferral';
import TimeLinePromoCode from './components/TimeLinePromoCode';
import TimeLineRegistration from './components/TimeLineRegistration';

const ASC = 'asc';
const DESC = 'desc';

const getDate = ({ date, lastDayEvent } = {}, invert) => {
  return lastDayEvent
    ? dateFNS[invert ? 'startOfDay' : 'endOfDay'](date)
    : date;
};

const sortByDate = (a, b, invert) => {
  const dateA = getDate(a, invert);
  const dateB = getDate(b, invert);

  let result = 0;

  if (dateA > dateB) {
    result = 1;
  } else if (dateA < dateB) {
    result = -1;
  }

  return invert ? result * -1 : result;
};

export const History = ({ data }) => {
  const [sort, setSort] = useState(DESC);
  const [childComponent, openChildComponent] = useChildComponent();

  const logs = useMemo(() => {
    let result = [TimeLineRegistration({ data, openChildComponent })];
    if (data?.playerInformation?.isFraud) {
      result.push(TimeLineFraud({ data }));
    }

    data?.eventInformation?.forEach((eventConfig) => {
      if (eventConfig.eventCompletionDate) {
        result.push(TimeLineEvent({ data: eventConfig, openChildComponent }));
      }
    });

    if (data?.reengagementInformation?.campaigns?.length) {
      data.reengagementInformation.campaigns.forEach((row) => {
        result.push(TimeLineReengagement({ data: row, openChildComponent }));
      });
    }
    if (data?.postbacksConversionsInformation?.postbacks?.length) {
      let businessPartner =
        data.postbacksConversionsInformation.businessPartner;
      data.postbacksConversionsInformation.postbacks.forEach((row) => {
        result.push(
          TimeLinePostBack({
            data: { businessPartner, ...row },
            openChildComponent,
          })
        );
      });
    }
    if (data?.postbacksConversionsInformation?.conversions?.length) {
      let businessPartner =
        data.postbacksConversionsInformation.businessPartner;
      data.postbacksConversionsInformation.conversions.forEach((row) => {
        result.push(
          TimeLineConversion({
            data: { businessPartner, ...row },
            openChildComponent,
          })
        );
      });
    }
    if (data?.recurrentEventInformation?.eventCompletions?.length) {
      const { event, eventCompletionRealCountry, eventCompletions } =
        data.recurrentEventInformation;

      eventCompletions.forEach((row) => {
        result.push(
          TimeLineRecurrentEvent({
            event,
            data: row,
            eventCompletionRealCountry,
          })
        );
      });
    }
    if (data?.assistedInstalls?.length) {
      data?.assistedInstalls.forEach((row) => {
        result.push(
          TimeLineContribution({
            data: row,
          })
        );
      });
    }
    if (data?.rereferralRequests?.length) {
      data?.rereferralRequests.forEach((row) => {
        result.push(
          TimeLineReferral({
            data: row,
          })
        );
      });
    }
    if (data?.promoCodesRedemptions?.length) {
      data?.promoCodesRedemptions.forEach((row) => {
        result.push(
          TimeLinePromoCode({
            data: row,
            openChildComponent,
          })
        );
      });
    }

    return result.sort((a, b) => sortByDate(a, b, sort === ASC));
  }, [data, sort, openChildComponent]);

  const handleChangeSort = () => {
    setSort(sort === ASC ? DESC : ASC);
  };

  return (
    <Grid container>
      <Grid item container spacing={2}>
        <Grid item container justifyContent={'space-between'} xs={12}>
          <Grid item>
            <Typography variant={'subtitle1'} sx={{ fontWeight: 500 }}>
              Activity log
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              icon={<ImportExportIcon />}
              onClick={handleChangeSort}
              tooltip={'Sort ' + (sort === ASC ? DESC : ASC)}
            />
          </Grid>
        </Grid>
        <Grid item container>
          {!logs.length ? (
            <NoResults fullHeight={true} inited={true} />
          ) : (
            <TimeLine dataCy={'TimeLine'} fullWidth={true}>
              {logs?.map((log, index) => {
                const summary = (
                  <LogText
                    date={log.date}
                    user={log.title}
                    dateTemplate={log.dateTemplate}
                    timeZone={'utc'}
                  />
                );
                return (
                  <TimeLineItem key={index} color={log.color}>
                    {log.content ? (
                      <TimeLineCollapse summary={summary} initialOpen={false}>
                        {log.content}
                      </TimeLineCollapse>
                    ) : (
                      summary
                    )}
                  </TimeLineItem>
                );
              })}
            </TimeLine>
          )}
        </Grid>
      </Grid>
      {childComponent}
    </Grid>
  );
};
